// §dexa-add-on — offer a DEXA body-composition scan alongside a treatment
// purchase, and an exclusive multi-session ladder once one is added.
//
// Owner pricing, 2026-10-07 (replaces the 2026-09-08 four-tier ladder):
//
//     1 scan    $99     (DexaSlim's own single-scan price is $179)
//     3 scans  $199     (public $399) — the bundle; ~$66/scan
//
// Two choices, not four: the single scan is the entry point and the 3-pack is
// the one upsell. Orders sold against the old ladder are identifiable by their
// priceListDate (2026-09-08) in the payload.
//
// The point of the add-on is that a GLP-1 patient has a body-composition
// question the medication itself cannot answer — how much of the loss is fat
// and how much is lean mass. Offering the scan at the moment they commit to
// treatment is the only time we have their attention and their card.
//
// ─────────────────────────────────────────────────────────────────────────────
// CENTS. This app prices everything in cents (`active.price`, `consultFeeCents`,
// SquarePaymentForm's `amount`). The DexaSlim booking app uses whole dollars for
// the same products. Mixing the two is a 100x error in whichever direction it
// happens, so nothing here is ever expressed in dollars.
// ─────────────────────────────────────────────────────────────────────────────
//
// FULFILMENT IS NOT BUILT. This module and its UI collect the intent and the
// money. Turning that into scan credits in the DexaSlim system requires a
// backend handoff that does not exist yet — see docs/FE_PRD_DEXA_BUNDLE_CREDITS.md.
// Nothing here should imply to a patient that an appointment is booked.

/**
 * §dexa-add-on kill switch. OFF unless REACT_APP_DEXA_ADDON_ENABLED === '1'.
 *
 * Default-off is deliberate and is the whole point: the add-on shipped live on
 * 2026-09-09 and takes money for scans that no system records — the backend
 * handoff in docs/BACKEND_DEXA_ADDON_HANDOFF_PRD.md does not exist yet. Merging
 * this without setting the flag TURNS THE ADD-ON OFF in production, which is the
 * intent. Turn it on when §4 of that PRD lands (the visit record persists
 * dexa_add_on), not before.
 *
 * Read at CALL TIME rather than module scope. CRA inlines process.env at build
 * so this is a constant in production, but in jest it stays a live lookup, which
 * is what lets the tests exercise both states without a module reset.
 */
export const isDexaAddOnEnabled = () =>
  process.env.REACT_APP_DEXA_ADDON_ENABLED === '1';

export const DEXA_PUBLIC_SINGLE_CENTS = 17900;

/**
 * The ladder. `sessions` is the credit count the patient is buying; it is the
 * number that must eventually reach `total_purchased` in the booking system.
 */
export const DEXA_TIERS = [
  { sessions: 1, cents: 9900, publicCents: 17900 },
  { sessions: 3, cents: 19900, publicCents: 39900 },
];

/** The current price list's date, stamped on every payload. */
export const DEXA_PRICE_LIST_DATE = '2026-10-07';

export const DEXA_ENTRY_TIER = DEXA_TIERS[0];

/**
 * §lab-panel-dexa — the /lab-panel offer (owner, 2026-10-20): one scan at
 * DexaSlim's own $179 price, New York residents only (the scan is at
 * DexaSlim NYC). A separate price list from the GLP-1 ladder above, so the
 * payload carries `offer: 'lab-panel'` and the BE must price it as such —
 * without that it answers dexa_price_changed (nothing charged).
 */
export const DEXA_LAB_PANEL_OFFER = 'lab-panel';
export const DEXA_LAB_PANEL_TIERS = [{ sessions: 1, cents: 17900, publicCents: 17900 }];
export const DEXA_LAB_PANEL_STATES = ['NY'];
export const labPanelDexaAvailable = (state) =>
  isDexaAddOnEnabled() && DEXA_LAB_PANEL_STATES.includes(String(state || '').toUpperCase());

const tiersFor = (offer) => (offer === DEXA_LAB_PANEL_OFFER ? DEXA_LAB_PANEL_TIERS : DEXA_TIERS);

/** Tier by session count, or null. `offer` picks the price list (GLP-1 by default). */
export const getDexaTier = (sessions, offer) =>
  tiersFor(offer).find((t) => t.sessions === Number(sessions)) || null;

/** The upgrades shown once the entry scan is added — everything above 1. */
export const getDexaUpgrades = () => DEXA_TIERS.filter((t) => t.sessions > 1);

/**
 * What the add-on contributes to the charge.
 *
 * Returns 0 for no selection, so callers can add this unconditionally rather
 * than branching — a branch is where a total silently loses a line item.
 *
 * @param {number|null} sessions the chosen tier, or null/0 for none
 */
export function dexaAddOnCents(sessions, offer) {
  // Belt-and-braces with the UI gate: if the feature is off, it contributes
  // nothing to the charge even if a session count somehow survived in state.
  if (!isDexaAddOnEnabled()) return 0;
  const tier = getDexaTier(sessions, offer);
  return tier ? tier.cents : 0;
}

/**
 * The saving against DexaSlim's public price, for display.
 * The entry scan saves $80 ($179 → $99); the 3-pack saves $200 ($399 → $199).
 */
export function dexaSaving(sessions, offer) {
  const tier = getDexaTier(sessions, offer);
  return tier ? tier.publicCents - tier.cents : 0;
}

/**
 * What the submit payload carries. `null` when nothing was added, so the field
 * is simply absent rather than present-and-zero — a backend reading
 * `dexaAddOn.sessions` must never see 0 and have to decide what that means.
 */
/**
 * BE #349 (DEXA credits): the DEXA offer is shown only when money moves for the
 * rest of the order. A $0 order (membership-covered consult) never reaches the
 * Square charge, so DEXA could not be paid for there.
 */
export function dexaOfferAvailable(preDexaCents) {
  return isDexaAddOnEnabled() && Number(preDexaCents) > 0;
}

/**
 * Credits the backend actually granted, from the payment response
 * (`dexaCredits`, BE #349). 0 when absent — e.g. while the backend's
 * DEXA_CREDITS_ENABLED is off, so the confirmation never promises a credit
 * that does not exist.
 */
export function dexaCreditsGranted(paymentResult) {
  const n = Number(paymentResult && (paymentResult.dexaCredits ?? paymentResult.dexaCreditsGranted));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export const DEXA_LOCATION = 'DexaSlim NYC (5 W 37th St)';
export const dexaCreditsCopy = (n) =>
  `${n} DEXA scan credit${n === 1 ? '' : 's'} — book anytime at ${DEXA_LOCATION} from your dashboard → Appointments.`;

export function dexaAddOnPayload(sessions, offer) {
  if (!isDexaAddOnEnabled()) return null;
  const tier = getDexaTier(sessions, offer);
  if (!tier) return null;
  return {
    ...(offer === DEXA_LAB_PANEL_OFFER ? { offer } : {}),
    sessions: tier.sessions,
    priceCents: tier.cents,
    // Stamped so a later reconciliation can tell which price list a given order
    // was sold against — these are hardcoded here, not read from a catalog, and
    // will drift the day someone changes them in one place only.
    priceListDate: DEXA_PRICE_LIST_DATE,
  };
}
